import dayjs from "dayjs";
import Decimal from "decimal.js";
import React from "react";

export const formatErrorMessage = (message) => {
  try {
    const parsedMessage =
      typeof message === "string" ? JSON.parse(message) : message;

    if (typeof parsedMessage === "object") {
      return Object.entries(parsedMessage)
        .map(([key, value]) => `${key}: ${value}`)
        .join("\n");
    }

    return message;
  } catch (e) {
    return message;
  }
};
export const colorPalette = [
  "#57A239",
  "#7391c8",
  "#7B1B25",
  "#FF33A8",
  "#A833FF",
  "#FF8C33",
];
 export const convertIntoDate = (date) => {
    const [datePart, timePart] = date.split(" ");
    const combinedDate = new Date(`${datePart} ${timePart.split(" ")[0]}`);
    const options = { month: "long" };
    const month = combinedDate.toLocaleString("en-US", options);
    const day = combinedDate.getDate();
    const year = combinedDate.getFullYear();

    const hours = String(combinedDate.getHours()).padStart(2, "0");
    const minutes = String(combinedDate.getMinutes()).padStart(2, "0");

    return `${month} ${day}, ${year}  ${hours}:${minutes}`;
  };

export const formatItemName = (itemName) => {
  return itemName?.replace(/([a-z])([A-Z])/g, "$1 $2");
};

// ERROR slice filter
export function filterData(data, timestamp, tablename) {
  return data
    .map((entry) => ({
      ...entry,
      errors: entry.errors.filter((error) => error.tablename !== tablename),
    }))
    .filter(
      (entry) => entry.timestamp !== timestamp || entry.errors.length > 0
    ); // Remove empty error entries
}
export function mergeDateAndTime(dateString, timeString) {
  const datePart = dateString ? dateString.slice(0, 10) : "";
  return `${datePart}  ${timeString}`;
}

export const combineArrays = (array1, array2) => {
  const combined = [...array1];

  array2.forEach((newItem) => {
    const existingIndex = combined.findIndex(
      (item) => item.tablename === newItem.tablename
    );

    if (existingIndex !== -1) {
      combined[existingIndex] = { ...combined[existingIndex], ...newItem };
    } else {
      combined.push(newItem);
    }
  });
  return combined;
};

export const generateFlowData = (data) => {
  const initialNodes = [];
  const initialEdges = [];

  // Create Owner Node (Uses username as label)
  initialNodes.push({
    id: `owner_${data.owner_code}`,
    type: "customNode",
    data: { label: data.username || "Owner" },
    position: { x: 400, y: 50 },
  });

  // Iterate over the ownergroups
  if (data?.ownergroups?.length) {
    data.ownergroups.forEach((group, groupIndex) => {
      const groupId = `group_${group.ownergroup_id}`;
      initialNodes.push({
        id: groupId,
        type: "customNode",
        data: { label: group.name || "Group" },
        position: { x: 200, y: 150 + groupIndex * 300 },
      });

      // Connect group to owner
      initialEdges.push({
        id: `edge_owner_${groupId}`,
        source: `owner_${data.owner_code}`,
        target: groupId,
      });

      // Handle fleets
      group?.fleets?.forEach((fleet, fleetIndex) => {
        const fleetId = `fleet_${fleet.fleet_id}`;
        initialNodes.push({
          id: fleetId,
          type: "customNode",
          data: { label: fleet.fleet_name || "Fleet" },
          position: { x: 400, y: 200 + groupIndex * 300 + fleetIndex * 150 },
        });

        // Connect fleet to group
        initialEdges.push({
          id: `edge_${groupId}_${fleetId}`,
          source: groupId,
          target: fleetId,
        });

        // Handle fleetShips
        fleet?.fleetShips?.forEach((ship, shipIndex) => {
          const shipId = `ship_${ship.imo_number}`; // ✅ Use imo_number as a unique id
          initialNodes.push({
            id: shipId,
            type: "customNode",
            data: { label: ship.name || "Ship" },
            position: {
              x: 600,
              y: 200 + groupIndex * 300 + fleetIndex * 150 + shipIndex * 100,
            },
          });

          // Connect ship to fleet
          initialEdges.push({
            id: `edge_${fleetId}_${shipId}`,
            source: fleetId,
            target: shipId,
          });
        });
      });
    });
  }

  return { initialNodes, initialEdges };
};

export function formatDateToYYYYMMDD(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const formatFieldName = (fieldName) => {
  let formattedName = fieldName?.length ? fieldName?.replace(/_/g, " "):"";
  formattedName = formattedName.replace(/\b\w/g, (char) => char.toUpperCase());
  return formattedName;
};
export function formatDateConversion(dateInput) {
  if (dateInput) {
    const date = new Date(dateInput);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }
}

export function formatCalendarDateConversion(dateInput) {
  if (dateInput) {
    const date = new Date(dateInput);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }
}

export const parseTime = (time) => {
  if (!time) return null;
  const [hours, minutes] = time.split(":");
  const date = new Date();
  date.setHours(hours, minutes, 0);
  return dayjs(date);
};

export const formatTime = (time) => {
  if (!time) return null;
  const hours = String(time.hour()).padStart(2, "0");
  const minutes = String(time.minute()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const SafeDecimal = (value) => {
  if (typeof Decimal === "function") {
    return new Decimal(value);
  } else if (Decimal.default && typeof Decimal.default === "function") {
    return new Decimal.default(value);
  } else {
    throw new Error("Unable to create Decimal instance");
  }
};

export const downloadJson = (jsonData, title) => {
  const jsonString = JSON.stringify(jsonData, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${title}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


     export const fieldRegex = /\b([a-zA-Z_][a-zA-Z0-9_]*\.[a-zA-Z0-9_]+)/g;

export function mergeArraysOptimized(arr1, arr2) {
  let array1 = arr1 || [];
  let array2 = arr2 || [];
  const map = new Map();
  const mergeData = (target, source) => ({ ...target, ...source });
  const concatArr =
    array1 && array2 ? [...array1, ...array2] : array1 || array2 || [];

  concatArr.forEach((item) => {
    if (map.has(item?.tablename)) {
      const existingItem = map.get(item?.tablename);
      existingItem.data = mergeData(existingItem.data, item.data);
    } else {
      map.set(item?.tablename, { ...item });
    }
  });

  // Convert the Map back to an array
  return Array.from(map.values());
}



 export function sanitizeFormulaExpression(expression) {
  let cleaned = expression.trim();

  // Remove whitespace
  cleaned = cleaned.replace(/\s+/g, "");

  if (!cleaned) return "0";

  // Replace sequences of repeated operators like '++' etc.
  cleaned = cleaned.replace(/([+\-*/^])\1+/g, '$1');

  // Collapse +-, -+, etc.
  cleaned = cleaned.replace(/\+\-/g, '-').replace(/\-\+/g, '-');

  // Remove leading operators except unary minus
  cleaned = cleaned.replace(/^[+*/^]+/, "");

  // Remove trailing operators or operator chains, e.g., "10+20++" -> "10+20"
  cleaned = cleaned.replace(/([+\-*/^])+$/, '');

  // If no digits remain (e.g., "++" or ""), return "0"
  if (!/\d/.test(cleaned)) return "0";

  return cleaned;
}
// Advanced Mathematical Expression Evaluator
export class MathExpressionEvaluator {
  constructor() {
    this.constants = {
      PI: SafeDecimal(Math.PI),
      E: SafeDecimal(Math.E),
    };

    this.RADIAN = this.constants.PI.div(180);

    this.mathFunctions = {
      sin: (x) => {
        const decX = SafeDecimal(x);
        return SafeDecimal(Math.sin(decX.toNumber()));
      },
      cos: (x) => {
        const decX = SafeDecimal(x);
        return SafeDecimal(Math.cos(decX.toNumber()));
      },
      tan: (x) => {
        const decX = SafeDecimal(x);
        return SafeDecimal(Math.tan(decX.toNumber()));
      },
      asin: (x) => {
        const decX = SafeDecimal(x);
        return SafeDecimal(Math.asin(decX.toNumber()));
      },
      acos: (x) => {
        const decX = SafeDecimal(x);
        return SafeDecimal(Math.acos(decX.toNumber()));
      },
      atan: (x) => {
        const decX = SafeDecimal(x);
        return SafeDecimal(Math.atan(decX.toNumber()));
      },

      // Logarithmic functions
      log: (x) => SafeDecimal(x).ln(),
      log10: (x) => SafeDecimal(x).log10(),

      // Exponential and root functions
      exp: (x) => SafeDecimal(x).exp(),
      sqrt: (x) => SafeDecimal(x).sqrt(),
      cbrt: (x) => SafeDecimal(x).pow(1 / 3),

      // Other mathematical functions
      abs: (x) => SafeDecimal(x).abs(),
      pow: (base, exp) => SafeDecimal(base).pow(SafeDecimal(exp)),
      max: (...args) => SafeDecimal.max(...args.map(SafeDecimal)),
      min: (...args) => SafeDecimal.min(...args.map(SafeDecimal)),

      // Radian conversion function
      radian: (x) => SafeDecimal(x).mul(this.RADIAN),
    };
  }

   

  // Tokenize the mathematical expression
  tokenize(expression) {
    expression = expression.replace(/\s+/g, "");

    // Replace constants
    Object.keys(this.constants).forEach((constant) => {
      expression = expression.replace(
        new RegExp(`\\b${constant}\\b`, "g"),
        this.constants[constant].toString()
      );
    });

    // Tokenization regex
    const tokenRegex = /([+\-*/()^])|(-?\d*\.?\d+)|(\b[a-zA-Z]+\b)/g;
    const tokens = [];
    let match;

    while ((match = tokenRegex.exec(expression)) !== null) {
      if (match[1]) {
        // Operator
        if (
          match[1] === "-" &&
          (tokens.length === 0 ||
            ["+", "-", "*", "/", "(", "^"].includes(tokens[tokens.length - 1]))
        ) {
          // Handle negative number
          tokenRegex.lastIndex--; // Look ahead for the number
          const negNumberMatch = tokenRegex.exec(expression);
          tokens.push(negNumberMatch[0]);
        } else {
          tokens.push(match[1]);
        }
      } else if (match[2]) {
        // Number
        tokens.push(match[2]);
      } else if (match[3]) {
        // Function or variable
        tokens.push(match[3]);
      }
    }

    return tokens;
  }

  // Convert infix notation to postfix (Shunting Yard algorithm)
  infixToPostfix(tokens) {
    const precedence = {
      "^": 4,
      "*": 3,
      "/": 3,
      "+": 2,
      "-": 2,
    };

    const output = [];
    const operators = [];
    const functions = Object.keys(this.mathFunctions);

    for (let token of tokens) {
      if (/^-?\d*\.?\d+$/.test(token)) {
        // Push numbers directly to the output
        output.push(token);
      } else if (functions.includes(token)) {
        // Function goes to operators stack
        operators.push(token);
      } else if (["+", "-", "*", "/", "^"].includes(token)) {
        // Handle operators with precedence
        while (
          operators.length > 0 &&
          operators[operators.length - 1] !== "(" &&
          precedence[operators[operators.length - 1]] >= precedence[token]
        ) {
          output.push(operators.pop());
        }
        operators.push(token);
      } else if (token === "(") {
        operators.push(token);
      } else if (token === ")") {
        // Pop operators until left parenthesis
        while (
          operators.length > 0 &&
          operators[operators.length - 1] !== "("
        ) {
          output.push(operators.pop());
        }
        operators.pop(); // Remove '('
        // Handle function call
        if (functions.includes(operators[operators.length - 1])) {
          output.push(operators.pop());
        }
      }
    }

    while (operators.length > 0) {
      output.push(operators.pop());
    }

    return output;
  }

  // Evaluate postfix expression
evaluatePostfix(postfix) {
  const stack = [];

  for (let token of postfix) {
    if (/^-?\d*\.?\d+$/.test(token)) {
      stack.push(SafeDecimal(token));
    } else if (["+", "-", "*", "/", "^"].includes(token)) {
      const b = stack.pop();
      const a = stack.pop();

      if (a === undefined || b === undefined) {
        throw new Error(
          `Invalid expression: insufficient operands for operator "${token}"`
        );
      }

      switch (token) {
        case "+":
          stack.push(a.plus(b));
          break;
        case "-":
          stack.push(a.minus(b));
          break;
        case "*":
          stack.push(a.mul(b));
          break;
        case "/":
          stack.push(a.div(b));
          break;
        case "^":
          stack.push(a.pow(b));
          break;
      }
    } else if (this.mathFunctions[token]) {
      const args = this.mathFunctions[token].length;
      const functionArgs = stack.splice(-args);

      if (functionArgs.length < args) {
        throw new Error(
          `Invalid expression: insufficient operands for function "${token}"`
        );
      }

      stack.push(this.mathFunctions[token](...functionArgs));
    }
  }

  if (stack.length !== 1) {
    throw new Error(`Invalid expression: leftover operands in stack.`);
  }

  return stack[0];
}

  // Main evaluation method
  evaluate(expression) {
    try {
      const tokens = this.tokenize(expression);

      const postfix = this.infixToPostfix(tokens);

      const result = this.evaluatePostfix(postfix);
      return result.toString();
    } catch (error) {
      console.error("Expression evaluation error:", error);
      throw new Error(`Invalid mathematical expression: ${expression}`);
    }
  }

  // Additional method for direct radian conversion
  convertToRadian(degrees) {
    return SafeDecimal(degrees).mul(this.RADIAN);
  }
}
 
export const evaluateExpression = (expression) => {
  const evaluator = new MathExpressionEvaluator();
   const cleanExpression = sanitizeFormulaExpression(expression);
   
  return evaluator.evaluate(cleanExpression);
};
